import React, { useEffect, useState } from 'react';
import AdminSidebar from '../components/AdminSidebar';
import { db } from '../firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { Plus, Pencil, Trash2, X } from 'lucide-react';

const STATUS_OPTS = [
  { value: 'daftar', label: 'Terdaftar' },
  { value: 'sembelih', label: 'Disembelih' },
  { value: 'packing', label: 'Dipacking' },
  { value: 'distribusi', label: 'Distribusi' },
  { value: 'selesai', label: 'Selesai' },
];

const EMPTY = { name: '', type: 'sapi', weight: '', color: '', age: '', status: 'daftar', notes: '' };

export default function AdminAnimals() {
  const [animals, setAnimals] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [filter, setFilter] = useState('all');

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'animals'), orderBy('createdAt', 'desc')), snap => {
      setAnimals(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  const openAdd = () => { setForm(EMPTY); setEditId(null); setShowModal(true); };
  const openEdit = (a) => { setForm({ name: a.name, type: a.type, weight: a.weight || '', color: a.color || '', age: a.age || '', status: a.status, notes: a.notes || '' }); setEditId(a.id); setShowModal(true); };

  const handleSave = async () => {
    if (!form.name) return;
    setSaving(true);
    try {
      if (editId) {
        await updateDoc(doc(db, 'animals', editId), { ...form, updatedAt: serverTimestamp() });
        showToast('Data hewan diperbarui');
      } else {
        await addDoc(collection(db, 'animals'), { ...form, createdAt: serverTimestamp() });
        showToast('Hewan berhasil ditambahkan');
      }
      setShowModal(false);
    } catch (e) { showToast('Gagal menyimpan: ' + e.message, 'error'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus data hewan ini?')) return;
    await deleteDoc(doc(db, 'animals', id));
    showToast('Data dihapus');
  };

  const filtered = filter === 'all' ? animals : animals.filter(a => a.type === filter);

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div><h1>Hewan Qurban</h1><p>Kelola data sapi dan kambing qurban.</p></div>
          <button className="btn btn-primary" onClick={openAdd}><Plus size={16} /> Tambah Hewan</button>
        </div>

        {/* Stats */}
        <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)', marginBottom: '1.25rem' }}>
          {[
            { label: 'Total', value: animals.length, color: 'var(--green-800)' },
            { label: 'Sapi', value: animals.filter(a => a.type === 'sapi').length, color: '#166534' },
            { label: 'Kambing', value: animals.filter(a => a.type === 'kambing').length, color: '#d97706' },
            { label: 'Selesai', value: animals.filter(a => a.status === 'selesai').length, color: '#16a34a' },
            { label: 'Proses', value: animals.filter(a => !['daftar', 'selesai'].includes(a.status)).length, color: '#0369a1' },
          ].map((s, i) => (
            <div key={i} className="stat-card">
              <div className="stat-label">{s.label}</div>
              <div className="stat-value" style={{ color: s.color, fontSize: 22 }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Filter */}
        <div style={{ display: 'flex', gap: 6, marginBottom: '1rem' }}>
          {[{ k: 'all', l: 'Semua' }, { k: 'sapi', l: '🐄 Sapi' }, { k: 'kambing', l: '🐏 Kambing' }].map(f => (
            <button key={f.k} className="btn btn-sm"
              style={{ background: filter === f.k ? 'var(--green-700)' : '#fff', color: filter === f.k ? '#fff' : 'var(--text)', border: '1.5px solid var(--border)' }}
              onClick={() => setFilter(f.k)}>{f.l}</button>
          ))}
        </div>

        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>No</th><th>Nama</th><th>Jenis</th><th>Berat (kg)</th><th>Warna</th><th>Usia</th><th>Status</th><th>Catatan</th><th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a, i) => (
                <tr key={a.id}>
                  <td style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{i + 1}</td>
                  <td style={{ fontWeight: 600 }}>{a.name}</td>
                  <td>{a.type === 'sapi' ? '🐄 Sapi' : '🐏 Kambing'}</td>
                  <td>{a.weight || '-'}</td>
                  <td>{a.color || '-'}</td>
                  <td>{a.age || '-'}</td>
                  <td>
                    <span className={`badge ${a.status === 'selesai' ? 'badge-green' : a.status === 'distribusi' ? 'badge-blue' : a.status === 'packing' ? 'badge-amber' : a.status === 'sembelih' ? 'badge-red' : 'badge-gray'}`}>
                      {STATUS_OPTS.find(s => s.value === a.status)?.label || a.status}
                    </span>
                  </td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 13, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.notes || '-'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-sm btn-ghost" onClick={() => openEdit(a)} style={{ padding: '4px 8px' }}><Pencil size={13} /></button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(a.id)} style={{ padding: '4px 8px' }}><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Belum ada data hewan</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <div className="modal-title">{editId ? 'Edit' : 'Tambah'} Hewan Qurban</div>
                <button className="modal-close" onClick={() => setShowModal(false)}><X size={18} /></button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
                {[
                  { label: 'Nama Hewan*', key: 'name', placeholder: 'Sapi 01 / Kambing Al-Barakah', span: 2 },
                  { label: 'Berat (kg)', key: 'weight', placeholder: '250', type: 'number' },
                  { label: 'Warna', key: 'color', placeholder: 'Coklat, Hitam, dll' },
                  { label: 'Usia (tahun)', key: 'age', placeholder: '2' },
                ].map(f => (
                  <div key={f.key} className="form-group" style={f.span ? { gridColumn: `span ${f.span}` } : {}}>
                    <label className="form-label">{f.label}</label>
                    <input type={f.type || 'text'} className="form-input" placeholder={f.placeholder}
                      value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} />
                  </div>
                ))}
                <div className="form-group">
                  <label className="form-label">Jenis</label>
                  <select className="form-input form-select" value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
                    <option value="sapi">🐄 Sapi</option>
                    <option value="kambing">🐏 Kambing</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-input form-select" value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                    {STATUS_OPTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">Catatan</label>
                  <textarea className="form-input" rows={3} placeholder="Kondisi hewan, catatan khusus..."
                    value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Batal</button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Menyimpan...' : 'Simpan'}</button>
              </div>
            </div>
          </div>
        )}

        {toast && (
          <div className="toast-container">
            <div className={`toast toast-${toast.type}`}>{toast.type === 'success' ? '✅' : '❌'} {toast.msg}</div>
          </div>
        )}
      </div>
    </div>
  );
}
